import React, { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faWallet,
  faMoneyBillTransfer,
  faSearch,
  faChevronLeft,
  faChevronRight,
  faCheckCircle,
  faTimesCircle,
  faClock,
} from "@fortawesome/free-solid-svg-icons";
import {
  Card,
  Row,
  Col,
  Form,
  Button,
  Table,
  Badge,
  InputGroup,
  Modal,
} from "@themesberg/react-bootstrap";

import {
  withdrawalRequests,
  adminApproveWithdrawalRequest,
  adminRejectWithdrawalRequest,
} from "../../../pages/api";

const ACTIVE_BUTTON_STYLE = {
  background: "#1D4E89",
  border: "none",
  borderRadius: "0.5rem",
  color: "#ffffff",
};

const REJECT_BUTTON_STYLE = {
  background: "#ef4444",
  border: "none",
  borderRadius: "0.5rem",
  color: "#ffffff",
};

const formatCurrency = (value) =>
  `₹${Number(value || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const formatDateTime = (value) => {
  if (!value) return "N/A" || "-";
  const normalized = String(value).replace(" ", "T");
  const date = new Date(normalized);
  if (Number.isNaN(date.getTime())) return value;

  return new Intl.DateTimeFormat("en-IN", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
};

const getStatusBadgeStyle = (status) => {
  switch (String(status || "").toLowerCase()) {
    case "approved":
    case "success":
    case "completed":
      return { background: "#10b981", color: "#ffffff" };
    case "pending":
      return { background: "#f59e0b", color: "#ffffff" };
    case "rejected":
    case "failed":
      return { background: "#ef4444", color: "#ffffff" };
    case "processed":
      return { background: "#0ea5e9", color: "#ffffff" };
    default:
      return { background: "#6b7280", color: "#ffffff" };
  }
};

const capitalize = (value) =>
  String(value || "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());

const mapWithdrawal = (item) => ({
  id: item?.id ?? Date.now(),
  userId: item?.user_id ?? "N/A",
  userName: item?.user_name || "N/A",
  amount: Number(item?.amount || 0),
  type: String(item?.type || "N/A"),
  status: String(item?.status || "N/A"),
  requestNote: item?.request_note || "N/A",
  adminNote: item?.admin_note || "N/A",
  processedAt: item?.processed_at || "N/A",
  createdAt: item?.created_at || "N/A",
  updatedAt: item?.updated_at || "N/A",
});

export default function AdminWithdrawalRequests() {
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [withdrawals, setWithdrawals] = useState([]);
  const [pagination, setPagination] = useState({
    current_page: 1,
    last_page: 1,
    per_page: 10,
    total: 0,
  });

  const [requestSummary, setRequestSummary] = useState({
    pending: 0,
    approved: 0,
    completed: 0,
    rejected: 0,
  });

  const [showActionModal, setShowActionModal] = useState(false);
  const [selectedWithdrawal, setSelectedWithdrawal] = useState(null);
  const [actionType, setActionType] = useState(""); // approve | reject
  const [adminNote, setAdminNote] = useState("");
  const [errors, setErrors] = useState({
    adminNote: "",
  });

  const searchDebounceRef = useRef(null);
  const firstSearchRenderRef = useRef(true);

  const fetchWithdrawalRequests = async (
    page = 1,
    perPage = 10,
    searchTerm = ""
  ) => {
    try {
      setLoading(true);

      const response = await withdrawalRequests({
        page,
        per_page: perPage,
        search: searchTerm,
      });

      const data = response?.data?.data || {};
      const apiWithdrawals = Array.isArray(data?.withdrawals)
        ? data.withdrawals
        : [];
      const apiPagination = data?.pagination || {};

      setWithdrawals(apiWithdrawals.map(mapWithdrawal));

      setRequestSummary({
        pending: Number(data?.pending_requests ?? 0),
        approved: Number(data?.approved_requests ?? 0),
        completed: Number(data?.completed_requests ?? 0),
        rejected: Number(data?.rejected_requests ?? 0),
      });

      setPagination({
        current_page: Number(apiPagination.current_page ?? page) || 1,
        last_page: Number(apiPagination.last_page ?? 1) || 1,
        per_page: Number(apiPagination.per_page ?? perPage) || perPage,
        total: Number(apiPagination.total ?? apiWithdrawals.length) || apiWithdrawals.length,
      });
    } catch (error) {
      console.error("Failed to fetch withdrawal requests:", error);
      toast.error(
        error?.response?.data?.message ||
        "Unable to load withdrawal requests."
      );
      setWithdrawals([]);
      setPagination({
        current_page: 1,
        last_page: 1,
        per_page: 10,
        total: 0,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchWithdrawalRequests(1, pagination.per_page, "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (firstSearchRenderRef.current) {
      firstSearchRenderRef.current = false;
      return;
    }

    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }

    searchDebounceRef.current = setTimeout(() => {
      fetchWithdrawalRequests(1, pagination.per_page, search.trim());
    }, 300);

    return () => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

 const openActionModal = (item, type) => {
  setSelectedWithdrawal(item);
  setActionType(type);
  setAdminNote("");
  setErrors({ adminNote: "" });
  setShowActionModal(true);
};

  const closeActionModal = () => {
    setShowActionModal(false);
    setSelectedWithdrawal(null);
    setActionType("");
    setAdminNote("");
    setErrors({ adminNote: "" });
  };

  const handleActionSubmit = async () => {
  if (!selectedWithdrawal?.id) return;

  try {
    setActionLoading(true);

    const payload =
      actionType === "reject" && adminNote.trim()
        ? { admin_note: adminNote.trim() }
        : {};

    if (actionType === "approve") {
      const response = await adminApproveWithdrawalRequest(selectedWithdrawal.id, payload);
      toast.success(
        response?.data?.message || "Withdrawal request approved successfully."
      );
    } else {
      const response = await adminRejectWithdrawalRequest(selectedWithdrawal.id, payload);
      toast.success(
        response?.data?.message || "Withdrawal request rejected successfully."
      );
    }

    closeActionModal();
    await fetchWithdrawalRequests(
      pagination.current_page,
      pagination.per_page,
      search.trim()
    );
  } catch (error) {
    console.error("Failed to update withdrawal request:", error);
    toast.error(
      error?.response?.data?.message || "Unable to update withdrawal request."
    );
  } finally {
    setActionLoading(false);
  }
};

  const getShowingRange = () => {
    const total = pagination.total || 0;
    const current = pagination.current_page || 1;
    const perPage = pagination.per_page || 10;

    if (total === 0) {
      return { start: 0, end: 0 };
    }

    const start = (current - 1) * perPage + 1;
    const end = Math.min(current * perPage, total);

    return { start, end };
  };

  const getPageItems = () => {
    const totalPages = pagination.last_page || 1;
    const current = pagination.current_page || 1;

    if (totalPages <= 5) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }

    if (current <= 2) {
      return [1, 2, 3, "...", totalPages];
    }

    if (current >= totalPages - 1) {
      return [1, "...", totalPages - 2, totalPages - 1, totalPages];
    }

    return [1, "...", current - 1, current, current + 1, "...", totalPages];
  };

  const { start, end } = getShowingRange();


  return (
    <>
      <div className="withdrawal-page">
        <div className="withdrawal-header py-4">
          <div className="withdrawal-header-top-row">
            {/* <h5 className="mb-0">Withdrawal Requests</h5> */}
            <h2 className="h4 mb-1">Withdrawal Requests</h2>
          </div>
        </div>


        <Row className="g-3 mb-4">
          <Col xs={12} sm={6} xl={6}>
            <Card className="border-0 shadow-sm rounded-4 h-100">
              <Card.Body className="p-3 p-md-4 d-flex justify-content-between align-items-start">
                <div>
                  <div className="text-muted small">Total Requests</div>
                  <h4 className="mb-0">{pagination.total}</h4>
                </div>
                <div className="withdrawal-icon-box">
                  <FontAwesomeIcon icon={faMoneyBillTransfer} />
                </div>
              </Card.Body>
            </Card>
          </Col>

          <Col xs={12} sm={6} xl={6}>
            <Card className="border-0 shadow-sm rounded-4 h-100">
              <Card.Body className="p-3 p-md-4 d-flex justify-content-between align-items-start">
                <div>
                  <div className="text-muted small">Pending</div>
                  <h4 className="mb-0">{requestSummary.pending}</h4>
                </div>
                <div className="withdrawal-icon-box">
                  <FontAwesomeIcon icon={faClock} />
                </div>
              </Card.Body>
            </Card>
          </Col>

          <Col xs={12} sm={6} xl={6}>
            <Card className="border-0 shadow-sm rounded-4 h-100">
              <Card.Body className="p-3 p-md-4 d-flex justify-content-between align-items-start">
                <div>
                  <div className="text-muted small">Approved</div>
                  <h4 className="mb-0">{requestSummary.approved}</h4>
                </div>
                <div className="withdrawal-icon-box">
                  <FontAwesomeIcon icon={faCheckCircle} />
                </div>
              </Card.Body>
            </Card>
          </Col>

          {/* <Col xs={12} sm={12} xl={12}>
    <Card className="border-0 shadow-sm rounded-4 h-100">
      <Card.Body className="p-3 p-md-4 d-flex justify-content-between align-items-start">
        <div>
          <div className="text-muted small">Completed Requests</div>
          <h4 className="mb-0">{requestSummary.completed}</h4>
        </div>
        <div className="withdrawal-icon-box">
          <FontAwesomeIcon icon={faWallet} />
        </div>
      </Card.Body>
    </Card>
  </Col> */}

          <Col xs={12} sm={6} xl={6}>
            <Card className="border-0 shadow-sm rounded-4 h-100">
              <Card.Body className="p-3 p-md-4 d-flex justify-content-between align-items-start">
                <div>
                  <div className="text-muted small">Rejected</div>
                  <h4 className="mb-0">{requestSummary.rejected}</h4>
                </div>
                <div className="withdrawal-icon-box">
                  <FontAwesomeIcon icon={faTimesCircle} />
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <Card className="border-0 shadow-sm rounded-4">
          <Card.Body className="p-4">
            <div className="withdrawal-section-top withdrawal-history-top">
              <div>
                <h5 className="mb-1">All Requests</h5>
                {/* <div className="text-muted small">
                  Approve or reject user withdrawal requests.
                </div> */}
              </div>

              <InputGroup className="withdrawal-search">
                <InputGroup.Text className="bg-white">
                  <FontAwesomeIcon icon={faSearch} className="text-muted" />
                </InputGroup.Text>
                <Form.Control
                  type="text"
                  placeholder="Search"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  style={{ boxShadow: "none" }}
                />
              </InputGroup>
            </div>

            <div className="table-responsive">
              <Table hover className="align-middle mb-0">
                <thead>
                  <tr>
                    <th>Sr No</th>
                    {/* <th>Request ID</th> */}
                    <th>Name</th>
                    {/* <th>Date</th> */}
                    <th className="text-end">Amount</th>
                    <th>Status</th>
                    <th>Remarks</th>
                    <th className="text-center">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={7} className="text-center text-muted py-5">
                        Loading withdrawal requests...
                      </td>
                    </tr>
                  ) : withdrawals.length > 0 ? (
                    withdrawals.map((item, index) => (
                      <tr key={item.id}>
                        {/* <td className="fw-semibold">{item.id}</td> */}
                        <td className="fw-semibold">{start + index}</td>
                        <td>{item.userName}</td>
                        {/* <td>{formatDateTime(item.createdAt)}</td> */}
                        <td className="text-end">{formatCurrency(item.amount)}</td>
                        <td>
                          <Badge
                            className="rounded-pill d-inline-flex align-items-center justify-content-center text-capitalize"
                            style={{
                              ...getStatusBadgeStyle(item.status),
                              minWidth: "5.5rem",
                              height: "2rem",
                              padding: "0 12px",
                            }}
                          >
                            {item.status}
                          </Badge>
                        </td>
                        <td>{item.requestNote || "N/A" || "-"}</td>
                        <td>
                          <div className="d-flex justify-content-center gap-2 flex-wrap">
                            <Button
                              type="button"
                              className="fw-semibold"
                              style={ACTIVE_BUTTON_STYLE}
                              disabled={ String(item.status).toLowerCase() !== "pending" || actionLoading }
                              onClick={() => openActionModal(item, "approve")}
                            >
                              <FontAwesomeIcon icon={faCheckCircle} />
                              {/* Approve */}
                            </Button>

                            <Button
                              type="button"
                              className="fw-semibold"
                              style={REJECT_BUTTON_STYLE}
                              disabled={ String(item.status).toLowerCase() !== "pending" || actionLoading }
                              onClick={() => openActionModal(item, "reject")}
                            >
                              <FontAwesomeIcon icon={faTimesCircle} />
                              {/* Reject */}
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={7} className="text-center text-muted py-5">
                        No withdrawal requests found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>

            <div className="d-flex justify-content-between align-items-center mt-3 flex-wrap gap-2">
              <div className="text-muted small">
                Showing {start} to {end} of {pagination.total} entries
              </div>

              <div className="d-flex align-items-center gap-2 flex-wrap">
                <Button
                  type="button"
                  variant="outline-primary"
                  disabled={pagination.current_page <= 1 || loading}
                  onClick={() =>
                    fetchWithdrawalRequests(
                      Math.max(1, pagination.current_page - 1),
                      pagination.per_page,
                      search.trim()
                    )
                  }
                  className="course-page-btn"
                >
                  <FontAwesomeIcon icon={faChevronLeft} />
                </Button>

                {getPageItems().map((item, index) =>
                  item === "..." ? (
                    <span key={`dots-${index}`} className="text-muted px-1">
                      ...
                    </span>
                  ) : (
                    <Button
                      key={item}
                      type="button"
                      variant="outline-primary"
                      disabled={loading}
                      onClick={() =>
                        fetchWithdrawalRequests(
                          item,
                          pagination.per_page,
                          search.trim()
                        )
                      }
                      className={`course-page-btn ${item === pagination.current_page
                        ? "course-page-btn-active"
                        : ""
                        }`}
                    >
                      {item}
                    </Button>
                  )
                )}

                <Button
                  type="button"
                  variant="outline-primary"
                  disabled={pagination.current_page >= pagination.last_page || loading}
                  onClick={() =>
                    fetchWithdrawalRequests(
                      Math.min(pagination.last_page, pagination.current_page + 1),
                      pagination.per_page,
                      search.trim()
                    )
                  }
                  className="course-page-btn"
                >
                  <FontAwesomeIcon icon={faChevronRight} />
                </Button>
              </div>
            </div>
          </Card.Body>
        </Card>
      </div>

      <Modal show={showActionModal} onHide={closeActionModal} centered backdrop="static">
        <Modal.Header closeButton>
          <Modal.Title>
            {actionType === "approve" ? "Approve Withdrawal" : "Reject Withdrawal"}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
  <div className="mb-3">
    <div className="text-muted small">User</div>
    <div className="fw-semibold">{selectedWithdrawal?.userName || "N/A" || "-"}</div>
  </div>

  <div className="mb-3">
    <div className="text-muted small">Amount</div>
    <div className="fw-semibold">
      {formatCurrency(selectedWithdrawal?.amount || 0)}
    </div>
  </div>

  {actionType === "reject" ? (
    <Form.Group>
      <Form.Label className="fw-semibold">Admin Note</Form.Label>
      <Form.Control
        as="textarea"
        rows={3}
        value={adminNote}
        onChange={(e) => setAdminNote(e.target.value)}
        placeholder="Reason for rejection"
        style={{ boxShadow: "none" }}
      />
    </Form.Group>
  ) : null}
</Modal.Body>

        <Modal.Footer>
          <Button onClick={closeActionModal} disabled={actionLoading}>
            Cancel
          </Button>
          <Button
            type="button"
            style={ACTIVE_BUTTON_STYLE}
            onClick={handleActionSubmit}
            disabled={actionLoading}
          >
            {actionLoading
              ? "Processing..."
              : actionType === "approve"
                ? "Approve"
                : "Reject"}
          </Button>
        </Modal.Footer>
      </Modal>

      <style>{`
        .withdrawal-page {
          width: 100%;
          padding-inline: 0;
        }

        .withdrawal-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 1rem;
          flex-wrap: nowrap;
        }

        .withdrawal-header-top-row {
          width: 100%;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 1rem;
          flex-wrap: wrap;
        }

        .withdrawal-search {
          width: 100%;
          max-width: 320px;
          flex: 0 1 320px;
          margin-left: auto;
        }

        .withdrawal-icon-box {
          width: 42px;
          height: 42px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 50%;
          background: rgba(0, 93, 251, 0.1);
          color: #005dfb;
          flex-shrink: 0;
        }

        .withdrawal-section-top {
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 0.75rem;
          flex-wrap: wrap;
          margin-bottom: 1rem;
        }

        .withdrawal-history-top {
          margin-bottom: 1rem;
        }

        .course-page-btn {
          width: 2rem;
          height: 2rem;
          padding: 0;
          border-radius: 0.5rem;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          line-height: 1;
        }

        .course-page-btn svg {
          font-size: 0.95rem;
        }

        @media (max-width: 991.98px) {
          .withdrawal-search {
            max-width: 280px;
            flex: 0 1 280px;
          }
        }

        @media (max-width: 767.98px) {
          .withdrawal-header {
            flex-wrap: wrap;
            align-items: flex-start;
          }

          .withdrawal-header-top-row {
            flex-direction: column;
            align-items: stretch;
          }

          .withdrawal-search {
            max-width: 100%;
            flex: 1 1 auto;
            margin-left: 0;
          }

          .withdrawal-section-top {
            flex-direction: column;
            align-items: stretch;
          }

          .withdrawal-history-top {
            flex-direction: row;
            align-items: center;
          }

          .table th,
          .table td {
            white-space: nowrap;
          }
        }

        @media (max-width: 575.98px) {
          .withdrawal-history-top {
            flex-direction: column;
            align-items: stretch;
          }
        }
      `}</style>
    </>
  );
}